import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "../client";
import {
  ProjectCreate,
  ProjectRead,
  ProjectUpdate,
  type ProjectCreate as ProjectCreateT,
  type ProjectUpdate as ProjectUpdateT,
} from "../schemas";
import { qk } from "../queryKeys";

const ProjectList = z.array(ProjectRead);

export function useProjects() {
  return useQuery({
    queryKey: qk.projects,
    queryFn: () => api.get("/api/projects", ProjectList),
  });
}

export function useProject(id: number | undefined) {
  return useQuery({
    queryKey: id ? qk.project(id) : qk.project(0),
    queryFn: () => api.get(`/api/projects/${id}`, ProjectRead),
    enabled: typeof id === "number" && id > 0,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectCreateT) =>
      api.post("/api/projects", ProjectCreate.parse(input), ProjectRead),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.projects });
    },
  });
}

export function useUpdateProject(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectUpdateT) =>
      api.patch(`/api/projects/${id}`, ProjectUpdate.parse(input), ProjectRead),
    onSuccess: (next) => {
      qc.setQueryData(qk.project(id), next);
      qc.invalidateQueries({ queryKey: qk.projects });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/projects/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.projects });
    },
  });
}
